import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchAdminStats, setFilters } from "@/store/slices/adminSlice";
import { Header } from "@/components/layout/Header";
import { DailySessionsChart } from "@/components/admin/dashboard/DailySessionsChart";
import { ScoreEvolutionChart } from "@/components/admin/dashboard/ScoreEvolutionChart";
import { TopScenariosTable } from "@/components/admin/dashboard/TopScenariosTable";
import { WeeklyEngagementChart } from "@/components/admin/dashboard/WeeklyEngagementChart";
import { TeamSkillsChart } from "@/components/admin/dashboard/TeamSkillsChart";
import { DashboardFilters } from "@/components/admin/dashboard/DashboardFilters";
import { CustomerSelector } from "@/components/admin/CustomerSelector";
import { Loader2 } from "lucide-react";

export default function AdminDashboard() {
  const dispatch = useAppDispatch();
  const { stats, currentStats, loading, error, filters } = useAppSelector(
    (state) => state.admin
  );

  useEffect(() => {
    dispatch(fetchAdminStats(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (startDate: string, endDate: string) => {
    dispatch(setFilters({ start_date: startDate, end_date: endDate }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-12">
        <div className="flex flex-col space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Team Performance Dashboard
              </h1>
              <p className="text-muted-foreground mt-1">
                Get real-time insights into your team's English communication
                progress
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center">
              <CustomerSelector />
              <DashboardFilters
                startDate={filters.start_date}
                endDate={filters.end_date}
                onFilterChange={handleFilterChange}
              />
            </div>
          </div>

          {loading && !stats ? (
            <div className="flex items-center justify-center h-[400px]">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-[400px] text-destructive">
              Error: {error}
            </div>
          ) : currentStats ? (
            <div className="grid grid-cols-1 gap-6">
              <DailySessionsChart data={currentStats.daily_sessions} />
              <ScoreEvolutionChart data={currentStats.score_evolution} />
              <TopScenariosTable data={currentStats.top_scenarios} />
              <WeeklyEngagementChart data={currentStats.weekly_engagement} />
              <div>
                <TeamSkillsChart data={currentStats.team_skills} />
              </div>
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}
